/**
 * 🍞 MGI Toast Notification Service
 * Sistema de notificaciones premium
 * Integración con vue-toastification
 */

import { useToast, POSITION, TYPE } from 'vue-toastification'

export interface ToastOptions {
  title?: string
  message: string
  type?: 'success' | 'error' | 'warning' | 'info'
  duration?: number
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center'
  action?: {
    label: string
    callback: () => void
  }
  persistent?: boolean
}

export class NotificationService {
  private static toast = useToast()

  // ✅ Notificación de éxito
  static success(options: ToastOptions | string) {
    const config = this.normalizeOptions(options, 'success')
    
    this.toast.success(config.message, {
      timeout: config.duration,
      position: this.getPosition(config.position),
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnFocusLoss: true,
      pauseOnHover: true,
      draggable: true,
      draggablePercent: 0.6,
      showCloseButtonOnHover: false,
      closeButton: "button",
      icon: "🎉",
      toastClassName: "mgi-toast mgi-toast-success"
    })
  }

  // ❌ Notificación de error
  static error(options: ToastOptions | string) {
    const config = this.normalizeOptions(options, 'error')
    
    this.toast.error(config.message, {
      timeout: config.persistent ? false : config.duration,
      position: this.getPosition(config.position),
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnFocusLoss: true,
      pauseOnHover: true,
      draggable: true,
      draggablePercent: 0.6,
      showCloseButtonOnHover: true,
      closeButton: "button",
      icon: "❌",
      toastClassName: "mgi-toast mgi-toast-error"
    })
  }

  // ⚠️ Notificación de advertencia
  static warning(options: ToastOptions | string) {
    const config = this.normalizeOptions(options, 'warning')
    
    this.toast.warning(config.message, {
      timeout: config.duration,
      position: this.getPosition(config.position),
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnFocusLoss: true,
      pauseOnHover: true,
      draggable: true,
      draggablePercent: 0.6,
      showCloseButtonOnHover: false,
      closeButton: "button",
      icon: "⚠️",
      toastClassName: "mgi-toast mgi-toast-warning"
    })
  }

  // ℹ️ Notificación informativa
  static info(options: ToastOptions | string) {
    const config = this.normalizeOptions(options, 'info')
    
    this.toast.info(config.message, {
      timeout: config.duration,
      position: this.getPosition(config.position),
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnFocusLoss: true,
      pauseOnHover: true,
      draggable: true,
      draggablePercent: 0.6,
      showCloseButtonOnHover: false,
      closeButton: "button",
      icon: "ℹ️",
      toastClassName: "mgi-toast mgi-toast-info"
    })
  }

  // 🔄 Notificación de loading
  static loading(message: string = 'Cargando...') {
    return this.toast.info(message, {
      timeout: false,
      position: POSITION.TOP_CENTER,
      hideProgressBar: true,
      closeOnClick: false,
      pauseOnFocusLoss: false,
      pauseOnHover: false,
      draggable: false,
      showCloseButtonOnHover: false,
      closeButton: false,
      icon: "⏳",
      toastClassName: "mgi-toast mgi-toast-loading"
    })
  }

  // 🗑️ Cerrar todas las notificaciones
  static clear() {
    this.toast.clear()
  }

  // 🗑️ Cerrar notificación específica
  static dismiss(toastId: any) {
    this.toast.dismiss(toastId)
  }

  // 🎯 Notificaciones específicas del sistema MGI

  // 👤 Usuario logueado
  static userLoggedIn(userName: string) {
    this.success({
      message: `¡Bienvenido, ${userName}!`,
      duration: 3000,
      position: 'top-right'
    })
  }

  // 🚪 Usuario deslogueado
  static userLoggedOut() {
    this.info({
      message: 'Sesión cerrada correctamente',
      duration: 2000,
      position: 'top-center'
    })
  }

  // 📊 Datos actualizados
  static dataRefreshed() {
    this.success({
      message: 'Datos actualizados correctamente',
      duration: 2000,
      position: 'bottom-right'
    })
  }

  // 🚨 Nueva incidencia
  static newIncidencia(title: string) {
    this.warning({
      message: `Nueva incidencia: ${title}`,
      duration: 5000,
      position: 'top-right',
      persistent: false
    })
  }

  // ✅ Incidencia resuelta
  static incidenciaResolved(title: string) {
    this.success({
      message: `Incidencia resuelta: ${title}`,
      duration: 4000,
      position: 'top-right'
    })
  }

  // 🔗 Error de conexión
  static connectionError() {
    this.error({
      message: 'Error de conexión con el servidor',
      persistent: true,
      position: 'top-center'
    })
  }

  // 🔄 Reconectado
  static reconnected() {
    this.success({
      message: 'Conexión restablecida',
      duration: 3000,
      position: 'top-center'
    })
  }

  // 🔐 Token expirado
  static tokenExpired() {
    this.warning({
      message: 'Tu sesión ha expirado. Redirigiendo al login...',
      duration: 3000,
      position: 'top-center'
    })
  }

  // 💾 Datos guardados
  static dataSaved() {
    this.success({
      message: 'Cambios guardados correctamente',
      duration: 2000,
      position: 'bottom-right'
    })
  }

  // 🗑️ Elemento eliminado
  static itemDeleted(itemType: string) {
    this.info({
      message: `${itemType} eliminado correctamente`,
      duration: 3000,
      position: 'bottom-right'
    })
  }

  // 📤 Archivo exportado
  static fileExported(fileName: string) {
    this.success({
      message: `Archivo ${fileName} exportado correctamente`,
      duration: 4000,
      position: 'bottom-right'
    })
  }

  // 🛠️ Utilidades privadas
  private static normalizeOptions(options: ToastOptions | string, defaultType: ToastOptions['type']): ToastOptions {
    if (typeof options === 'string') {
      return {
        message: options,
        type: defaultType,
        duration: 4000,
        position: 'top-right'
      }
    }
    
    return {
      type: defaultType,
      duration: 4000,
      position: 'top-right',
      ...options
    }
  }

  private static getPosition(position?: ToastOptions['position']) {
    switch (position) {
      case 'top-left': return POSITION.TOP_LEFT
      case 'top-right': return POSITION.TOP_RIGHT
      case 'top-center': return POSITION.TOP_CENTER
      case 'bottom-left': return POSITION.BOTTOM_LEFT
      case 'bottom-right': return POSITION.BOTTOM_RIGHT
      case 'bottom-center': return POSITION.BOTTOM_CENTER
      default: return POSITION.TOP_RIGHT
    }
  }
}

// 🌍 Export para uso global
export default NotificationService
